import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Receipt, PlusCircle, TrendingUp, Wallet, ReceiptText,
  PiggyBank, Ban, Pencil, Paperclip, X, FileText, Info,
} from 'lucide-react';
import { edificiosApi, coworkForestaApi } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn, formatDate } from '../../lib/utils';
import CoworkLiquidacionTrimestral from '../../components/cowork/CoworkLiquidacionTrimestral';
import CoworkVistaInversor from '../../components/cowork/CoworkVistaInversor';

const SUBTABS = [
  { id: 'configuracion', label: 'Configuración' },
  { id: 'liquidacion', label: 'Liquidación' },
  { id: 'liquidacion_trimestral', label: 'Liquidación Trimestral' },
  { id: 'vista_inversor', label: 'Vista Inversor' },
  { id: 'kpis', label: 'KPIs' },
];

const ALCANCES = [
  { value: 'A_54', label: 'Entre los 54 puestos' },
  { value: 'B_alquiler', label: 'Solo actividad de alquiler' },
  { value: 'C_pool', label: 'Solo puestos administrados' },
  { value: 'D_seleccionados', label: 'Puestos seleccionados' },
];
const ALCANCE_LABEL = Object.fromEntries(ALCANCES.map((a) => [a.value, a.label]));

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function fmtMoney(n) {
  const v = Number(n || 0);
  return `$ ${v.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function periodoLabel(periodo) {
  if (!periodo) return '';
  const [y, m] = periodo.split('-');
  return `${MESES_ES[parseInt(m, 10) - 1]} ${y}`;
}

// Lista de meses: desde 2026-01 hasta 2 meses adelante de hoy.
function buildMeses() {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 1);
  const out = [];
  let cur = new Date(2026, 0, 1);
  while (cur <= end) {
    const p = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
    out.push(p);
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return out.reverse();
}

function currentPeriodo() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

const KpiCard = ({ label, value, icon: Icon, tone = 'slate', sub, testid }) => {
  const tones = {
    slate: 'text-slate-900', emerald: 'text-emerald-700', rose: 'text-rose-700',
    amber: 'text-amber-700', blue: 'text-blue-700',
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
      </div>
      <div className={`mt-1.5 text-2xl font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
};

const EMPTY_FORM = {
  nombre: '', importe: '', alcance: 'A_54', mes: currentPeriodo(),
  fecha: currentPeriodo() + '-01', fecha_pago: '', observaciones: '', puestos: [],
};

export default function CoworkForesta({ edificioId: edificioIdProp }) {
  const meses = React.useMemo(buildMeses, []);
  const [edificioId, setEdificioId] = useState(edificioIdProp || null);
  const [periodo, setPeriodo] = useState(currentPeriodo());
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('liquidacion');

  // Form de alta/edición de gasto
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (edificioIdProp) { setEdificioId(edificioIdProp); return; }
    (async () => {
      try {
        const res = await edificiosApi.list();
        const foresta = (res.data || []).find((e) => e.modelo_administracion === 'propietario_factura_directo');
        if (foresta) setEdificioId(foresta.edificio_id);
        else { toast.error('No se encontró el edificio Cowork Foresta'); setLoading(false); }
      } catch { toast.error('Error cargando edificios'); setLoading(false); }
    })();
  }, [edificioIdProp]);

  const cargar = useCallback(async (eid, per) => {
    setLoading(true);
    try {
      const [rRes, gRes, pRes] = await Promise.all([
        coworkForestaApi.resumenMensual(eid, per),
        coworkForestaApi.listGastos(eid, per),
        coworkForestaApi.listPuestos(eid),
      ]);
      setResumen(rRes.data);
      setGastos(gRes.data.items || []);
      setPuestos(pRes.data.puestos || []);
    } catch { toast.error('Error cargando el resumen del cowork'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (edificioId) cargar(edificioId, periodo); }, [edificioId, periodo, cargar]);

  const resetForm = () => { setForm({ ...EMPTY_FORM, mes: periodo, fecha: periodo + '-01' }); setFile(null); setEditId(null); };

  const startEdit = (g) => {
    setEditId(g.gasto_id);
    setForm({
      nombre: g.nombre || '', importe: String(g.importe ?? ''), alcance: g.alcance || 'A_54',
      mes: g.periodo || periodo, fecha: g.fecha || '', fecha_pago: g.fecha_pago || '',
      observaciones: g.observaciones || '', puestos: g.puestos_alcanzados || [],
    });
    setFile(null);
    document.getElementById('cowork-gasto-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitGasto = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('Ingresá el nombre del gasto'); return; }
    if (!(parseFloat(form.importe) > 0)) { toast.error('El importe debe ser mayor a 0'); return; }
    if (form.alcance === 'D_seleccionados' && form.puestos.length === 0) {
      toast.error('Seleccioná al menos un puesto'); return;
    }
    setSaving(true);
    const payload = {
      nombre: form.nombre.trim(),
      importe: parseFloat(form.importe),
      alcance: form.alcance,
      periodo: form.mes,
      fecha: form.fecha || undefined,
      fecha_pago: form.fecha_pago || '',
      observaciones: form.observaciones || '',
      puestos_alcanzados: form.alcance === 'D_seleccionados' ? form.puestos.join(',') : '',
    };
    try {
      if (editId) {
        await coworkForestaApi.editarGasto(edificioId, editId, payload, file);
        toast.success('Gasto actualizado');
      } else {
        await coworkForestaApi.crearGasto(edificioId, payload, file);
        toast.success('Gasto cargado');
      }
      resetForm();
      cargar(edificioId, periodo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar el gasto');
    } finally { setSaving(false); }
  };

  const anular = async (g) => {
    if (!window.confirm(`¿Anular el gasto "${g.nombre}" (${fmtMoney(g.importe)})?`)) return;
    try {
      await coworkForestaApi.anularGasto(edificioId, g.gasto_id);
      toast.success('Gasto anulado');
      if (editId === g.gasto_id) resetForm();
      cargar(edificioId, periodo);
    } catch { toast.error('No se pudo anular el gasto'); }
  };

  const togglePuesto = (uid) => {
    setForm((f) => ({
      ...f,
      puestos: f.puestos.includes(uid) ? f.puestos.filter((x) => x !== uid) : [...f.puestos, uid],
    }));
  };

  const resultado = resumen?.resultado ?? 0;

  if (loading && !resumen) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500" data-testid="cowork-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando Cowork…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cowork-foresta-tab">
      {/* Encabezado: título + solapas internas en la misma línea */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-200 pb-2">
        <h2 className="text-lg font-semibold text-slate-900">Cowork</h2>
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist" data-testid="cowork-subtabs">
          {SUBTABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeSubTab === t.id}
              onClick={() => setActiveSubTab(t.id)}
              data-testid={`cowork-subtab-${t.id}`}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                activeSubTab === t.id
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Configuración — vacía por ahora */}
      {activeSubTab === 'configuracion' && (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="cowork-tab-configuracion">
          Configuración — próximamente.
        </div>
      )}

      {/* KPIs — vacía por ahora */}
      {activeSubTab === 'kpis' && (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="cowork-tab-kpis">
          KPIs — próximamente.
        </div>
      )}

      {/* Liquidación Trimestral — rendición del pool (tabla editable) */}
      {activeSubTab === 'liquidacion_trimestral' && (
        <div data-testid="cowork-tab-liquidacion-trimestral">
          <CoworkLiquidacionTrimestral edificioId={edificioId} />
        </div>
      )}

      {/* Vista Inversor — reporte de rendición por inversor */}
      {activeSubTab === 'vista_inversor' && (
        <div data-testid="cowork-tab-vista-inversor">
          <CoworkVistaInversor edificioId={edificioId} />
        </div>
      )}

      {/* Liquidación — todo el contenido actual (sin cambios de datos/lógica) */}
      {activeSubTab === 'liquidacion' && (
      <div className="space-y-6" data-testid="cowork-tab-liquidacion">
      {/* Selector de mes */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-500">Mes</label>
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            data-testid="cowork-mes-selector"
          >
            {meses.map((p) => <option key={p} value={p}>{periodoLabel(p)}</option>)}
          </select>
        </div>
      </div>

      {/* KPIs económicos */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Ingresos cobrados" value={fmtMoney(resumen?.ingresos_cobrados)} icon={TrendingUp}
          tone="emerald" sub={`${resumen?.alquilados || 0} alquilados · pool`} testid="kpi-ingresos" />
        <KpiCard label="Expensas" value={fmtMoney(resumen?.expensas_pool)} icon={ReceiptText}
          tone="rose" sub={resumen?.expensas_total > 0 ? `${resumen?.administrados}/${resumen?.total_puestos} del total` : 'Sin liquidación aprobada'} testid="kpi-expensas" />
        <KpiCard label="Otros gastos" value={fmtMoney(resumen?.gastos_pool)} icon={Wallet}
          tone="rose" sub={`${resumen?.gastos_count || 0} gasto(s)`} testid="kpi-gastos" />
        <KpiCard label="Fondo de reserva" value={fmtMoney(0)} icon={PiggyBank}
          tone="slate" sub="Se aplica en el cierre trimestral" testid="kpi-fondo" />
        <KpiCard label="Resultado del mes" value={fmtMoney(resultado)} icon={TrendingUp}
          tone={resultado >= 0 ? 'emerald' : 'rose'} sub="Ingresos − Expensas − Gastos" testid="kpi-resultado" />
      </div>

      {/* Formulario de carga de gasto */}
      <Card className="p-5" id="cowork-gasto-form" data-testid="cowork-gasto-form">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-slate-800">
            {editId ? 'Editar gasto' : 'Cargar gasto del cowork'}
          </h3>
          {editId && (
            <button onClick={resetForm} className="ml-auto text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="cowork-gasto-cancel">
              <X className="w-3.5 h-3.5" /> Cancelar edición
            </button>
          )}
        </div>
        <form onSubmit={submitGasto} className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-500 block mb-1">Nombre del gasto *</label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Limpieza, Marketing, Internet, Reparación…" data-testid="gasto-nombre" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Importe *</label>
            <Input type="number" min="0" step="0.01" value={form.importe}
              onChange={(e) => setForm({ ...form, importe: e.target.value })} placeholder="0" data-testid="gasto-importe" />
          </div>

          <div>
            <label className="text-xs text-slate-500 block mb-1">Tipo de distribución *</label>
            <select value={form.alcance} onChange={(e) => setForm({ ...form, alcance: e.target.value })}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid="gasto-alcance">
              {ALCANCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Mes al que corresponde</label>
            <select value={form.mes} onChange={(e) => setForm({ ...form, mes: e.target.value })}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid="gasto-mes">
              {meses.map((p) => <option key={p} value={p}>{periodoLabel(p)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Comprobante</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-xs text-slate-600 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-slate-700 file:text-xs hover:file:bg-slate-200"
              data-testid="gasto-comprobante" />
          </div>

          <div>
            <label className="text-xs text-slate-500 block mb-1">Fecha</label>
            <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="gasto-fecha" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Fecha de pago</label>
            <Input type="date" value={form.fecha_pago} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} data-testid="gasto-fecha-pago" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Observaciones</label>
            <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
              placeholder="Opcional" data-testid="gasto-observaciones" />
          </div>

          {form.alcance === 'D_seleccionados' && (
            <div className="md:col-span-3">
              <label className="text-xs text-slate-500 block mb-1">
                Puestos seleccionados * <span className="text-slate-400">({form.puestos.length} elegido/s)</span>
              </label>
              <div className="max-h-40 overflow-auto rounded-md border border-slate-200 p-2 grid grid-cols-3 md:grid-cols-6 gap-1.5" data-testid="gasto-puestos-select">
                {puestos.map((p) => (
                  <label key={p.unidad_id} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-pointer ${form.puestos.includes(p.unidad_id) ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-slate-50 text-slate-600'}`}>
                    <input type="checkbox" checked={form.puestos.includes(p.unidad_id)}
                      onChange={() => togglePuesto(p.unidad_id)} data-testid={`gasto-puesto-${p.unidad_id}`} />
                    {p.nombre_unidad}{!p.en_administracion && <span className="text-slate-400">(uso propio)</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-3 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="gasto-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? <Pencil className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />)}
              <span className="ml-1.5">{editId ? 'Guardar cambios' : 'Agregar gasto'}</span>
            </Button>
          </div>
        </form>
      </Card>

      {/* Listado de gastos del mes */}
      <section>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Gastos de {periodoLabel(periodo)}</h3>
        <Card className="overflow-hidden">
          {gastos.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400" data-testid="cowork-gastos-empty">
              No hay gastos cargados para este mes.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Nombre</th>
                    <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase">Importe</th>
                    <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Distribución</th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase">Comprob.</th>
                    <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {gastos.map((g) => (
                    <tr key={g.gasto_id} className="hover:bg-slate-50/60" data-testid={`gasto-row-${g.gasto_id}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium text-slate-900">{g.nombre}</div>
                        {g.observaciones && <div className="text-[11px] text-slate-400">{g.observaciones}</div>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-slate-800">{fmtMoney(g.importe)}</td>
                      <td className="py-2 px-3">
                        <span className="inline-block px-2 py-0.5 rounded text-[11px] ring-1 bg-slate-50 text-slate-600 ring-slate-200">
                          {ALCANCE_LABEL[g.alcance] || g.alcance}
                        </span>
                        {g.alcance === 'D_seleccionados' && (
                          <span className="ml-1 text-[11px] text-slate-400">({(g.puestos_alcanzados || []).length})</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center text-slate-600">{g.fecha ? formatDate(g.fecha) : '—'}</td>
                      <td className="py-2 px-3 text-center">
                        {g.archivo_id ? (
                          <a href={coworkForestaApi.comprobanteUrl(g.archivo_id)} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-7 h-7 rounded text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Ver comprobante" data-testid={`gasto-comprob-${g.gasto_id}`}>
                            <Paperclip className="w-4 h-4" />
                          </a>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => startEdit(g)} className="w-7 h-7 rounded flex items-center justify-center text-slate-500 hover:text-blue-700 hover:bg-blue-50"
                            title="Editar" data-testid={`gasto-editar-${g.gasto_id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => anular(g)} className="w-7 h-7 rounded flex items-center justify-center text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                            title="Anular" data-testid={`gasto-anular-${g.gasto_id}`}>
                            <Ban className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>

      {/* Resultado del mes */}
      <Card className="p-5" data-testid="cowork-resultado-box">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-emerald-600" /> Resultado de {periodoLabel(periodo)}
        </h3>
        <div className="space-y-1.5 text-sm max-w-md">
          <div className="flex justify-between"><span className="text-slate-600">Ingresos cobrados</span><span className="font-mono text-emerald-700 tabular-nums">{fmtMoney(resumen?.ingresos_cobrados)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">− Expensas (pool)</span><span className="font-mono text-rose-700 tabular-nums">{fmtMoney(resumen?.expensas_pool)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">− Otros gastos (pool)</span><span className="font-mono text-rose-700 tabular-nums">{fmtMoney(resumen?.gastos_pool)}</span></div>
          <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
            <span className="font-semibold text-slate-800">Resultado del mes</span>
            <span className={`font-mono text-lg font-bold tabular-nums ${resultado >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="resultado-valor">{fmtMoney(resultado)}</span>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-400 flex items-start gap-1 max-w-md">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          El fondo de reserva no se descuenta mensualmente: se calcula sobre el resultado trimestral positivo al cerrar la rendición.
        </p>
      </Card>
      </div>
      )}
    </div>
  );
}
